import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FlaskConical, RefreshCw, ChevronLeft, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

/**
 * LabManage (V3.18) — Vue employeur pour gérer les soumissions au Lab.
 */
export default function LabManage() {
  const { user } = useAuth();
  const isAllowed = ["employeur", "entrepreneur", "admin", "super-admin"].includes(user?.role);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/lab/submissions/mine");
      setSubs(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const review = async (id, action) => {
    const feedback = action === "reject"
      ? window.prompt("Motif du refus (optionnel) :", "")
      : window.prompt("Message de félicitations (optionnel) :", "Bien joué !");
    setBusy(id);
    try {
      await api.post(`/v1/lab/submissions/${id}/review`, {
        action, feedback: feedback || undefined,
      });
      toast.success(action === "accept" ? "Candidature acceptée" : "Candidature refusée");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const grouped = useMemo(() => {
    const byStatus = { pending: [], accepted: [], rejected: [], completed: [] };
    for (const s of subs) (byStatus[s.status] || (byStatus[s.status] = [])).push(s);
    return byStatus;
  }, [subs]);

  if (!isAllowed) {
    return <PrivateLayout title="Le Lab · Gestion">
      <div className="afr-empty" data-testid="lab-manage-forbidden"><div className="afr-empty__title">Réservé aux employeurs</div></div>
    </PrivateLayout>;
  }

  return (
    <PrivateLayout
      title={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <FlaskConical size={20} /> Le Lab · Candidatures reçues
      </span>}
      breadcrumbs={[{ to: "/lab", label: "Le Lab" }, { label: "Gérer" }]}
      actions={
        <div style={{ display: "flex", gap: 6 }}>
          <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={load} data-testid="lab-manage-refresh">
            <RefreshCw size={12} /> Actualiser
          </button>
          <Link to="/lab" className="afr-btn afr-btn--outline afr-btn--sm">
            <ChevronLeft size={14} /> Le Lab
          </Link>
        </div>
      }
    >
      <div data-testid="lab-manage-page">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
          <Kpi label="En attente" value={grouped.pending.length} color="#B45309" testid="lab-kpi-pending" />
          <Kpi label="Acceptées" value={grouped.accepted.length} color="#009640" testid="lab-kpi-accepted" />
          <Kpi label="Complétées" value={grouped.completed.length} color="#065F46" testid="lab-kpi-completed" />
          <Kpi label="Refusées" value={grouped.rejected.length} color="#94a3b8" testid="lab-kpi-rejected" />
        </div>

        {loading ? (
          <div>Chargement…</div>
        ) : subs.length === 0 ? (
          <div className="afr-empty" data-testid="lab-manage-empty">
            <div className="afr-empty__title">Aucune candidature reçue pour le moment.</div>
          </div>
        ) : (
          <section className="afr-card" style={{ padding: 12 }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
                <thead>
                  <tr style={{ background: "#F1F5F9" }}>
                    <th style={th}>Talent</th>
                    <th style={th}>Item</th>
                    <th style={th}>Compétence</th>
                    <th style={th}>Livrable</th>
                    <th style={th}>Statut</th>
                    <th style={th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.id} data-testid={`lab-sub-row-${s.id}`}>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{s.talent_name || "—"}</div>
                        {s.note && <div style={{ fontSize: "0.72rem", color: "#64748b" }}>{s.note}</div>}
                      </td>
                      <td style={td}>
                        <div>{s.item_title}</div>
                        <span style={{
                          padding: "1px 6px", borderRadius: 4, fontSize: "0.68rem",
                          background: s.item_kind === "challenge" ? "#FEF3C7" : "#DCFCE7",
                          color: s.item_kind === "challenge" ? "#78350F" : "#065F46",
                        }}>
                          {s.item_kind === "challenge" ? "Défi" : "Projet Pro"}
                        </span>
                      </td>
                      <td style={td}>{s.skill_focus}</td>
                      <td style={td}>
                        {s.deliverable_url ? (
                          <a href={s.deliverable_url} target="_blank" rel="noopener noreferrer"
                             data-testid={`lab-sub-deliverable-${s.id}`}
                             style={{ color: "#009640", fontSize: "0.85rem" }}>
                            Voir <ExternalLink size={11} />
                          </a>
                        ) : "—"}
                      </td>
                      <td style={td}>
                        <StatusBadge status={s.status} />
                        {s.escrow_id && (
                          <div style={{ marginTop: 3 }}>
                            <Link to={`/escrow/${s.escrow_id}`}
                                  data-testid={`lab-sub-escrow-${s.id}`}
                                  style={{ fontSize: "0.75rem", color: "#009640" }}>
                              Escrow →
                            </Link>
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        {s.status === "pending" && (
                          <div style={{ display: "flex", gap: 4 }}>
                            <button
                              className="afr-btn afr-btn--primary afr-btn--sm"
                              onClick={() => review(s.id, "accept")}
                              disabled={busy === s.id}
                              data-testid={`lab-accept-${s.id}`}
                            >
                              <CheckCircle2 size={12} /> Accepter
                            </button>
                            <button
                              className="afr-btn afr-btn--outline afr-btn--sm"
                              onClick={() => review(s.id, "reject")}
                              disabled={busy === s.id}
                              data-testid={`lab-reject-${s.id}`}
                            >
                              <XCircle size={12} /> Refuser
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </PrivateLayout>
  );
}

function Kpi({ label, value, color, testid }) {
  return (
    <div className="afr-card" style={{ padding: 10, borderLeft: `3px solid ${color}` }} data-testid={testid}>
      <div style={{ fontSize: "0.72rem", color: "#64748b" }}>{label}</div>
      <div style={{ fontSize: "1.4rem", fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const c = {
    pending: ["#FEF3C7", "#78350F"], accepted: ["#DCFCE7", "#065F46"],
    completed: ["#DCFCE7", "#065F46"], rejected: ["#FEE2E2", "#991B1B"],
  }[status] || ["#E2E8F0", "#475569"];
  return (
    <span data-testid={`lab-status-${status}`}
          style={{ padding: "2px 8px", borderRadius: 999, background: c[0], color: c[1], fontWeight: 700, fontSize: "0.72rem" }}>
      {status}
    </span>
  );
}

const th = { textAlign: "left", padding: "8px 10px", fontWeight: 600, fontSize: "0.78rem" };
const td = { padding: "8px 10px", borderBottom: "1px solid #E2E8F0", verticalAlign: "top" };
